"use client"

import Title from "@/components/common/Typography/Title"
import { Col, Row } from "antd"
import Image from "next/image"
import FormSearchFlightMobile from "./FormSearchFlightMobile"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import PopupSelectPassenger, { PopupSelectPassengerRef } from "./PopupSelectPassenger"
import PopupSelectClass from "./PopupSelectClass"
import DrawerSelectPlacement, { DrawerSelectPlacementRef } from "./DrawerSelectPlacement"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import DrawerSelectDate, { DrawerSelectDateRef } from "@/components/common/DrawerSelectDate"

import { DrawerFlightDetailRef } from "../../flight-detail/mobile/DrawerFlightDetail"
import PageHeaderMobile from "@/components/common/PageHeaderMobile"
import { useAirports } from "@/hooks/flight/useAirports"
import { getMinFlights } from "@/utils/flight/getMinFlights"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { FlightSearchPrefill } from "../../flights/landing/flightSearchPrefill"
import { useEffect, useRef } from "react"
import dayjs from "dayjs"
import { usePathname } from "next/navigation"
import { getProductLandingTitleKey } from "@/utils/app/getProductLandingTitleKey"

export const searchFlightDrawers: {
  flights: any,
  flightDetail: DrawerFlightDetailRef | null,
  selectPassenger: PopupSelectPassengerRef | null,
  selectPlacementTo: DrawerSelectPlacementRef | null,
  selectPlacementFrom: DrawerSelectPlacementRef | null,
  selectClass: DrawerSelectPlacementRef | null,
  selectDate: DrawerSelectDateRef | null,
} = {
  flights: null,
  selectPassenger: null,
  selectPlacementTo: null,
  selectPlacementFrom: null,
  selectClass: null,
  flightDetail: null,
  selectDate: null,
}

const DrawerFormSearchFlightSkeleton = ({ autoHeight, prefill }: { autoHeight?: boolean; prefill?: FlightSearchPrefill }) => {
  const router = useTERouter()
  const pathname = usePathname()
  const hidePageHeader = !!getProductLandingTitleKey(pathname)
  useAirports()
  const dispatch = useAppDispatch()
  const airports = useAppSelector((state) => state.flight.get_airports.data)
  const didPrefillRef = useRef(false)
  const searchMin = useAppSelector(state => Object.values(state.flight.minFlightsByMonths).map(obj => {
    const values = Object.values(obj)
    return values.reduce((obj1, fare) => {
      return {
        ...obj1,
        [fare.date]: `${Math.floor(fare.lowestFare / 1000)}k`
      }
    }, {})
  }).reduce((obj, minFares) => ({ ...obj, ...minFares }), {}))
  const selectedFligthInputIndex = useAppSelector(state => state.flight.selectedFligthInputIndex)
  const selectedInputFlight = useAppSelector(state => state.flight.inputFlights[state.flight.selectedFligthInputIndex])
  const checkedItinerary = useAppSelector(state => state.flight.checkedItinerary)
  const dateFrom = useAppSelector(state => state.flight.inputFlights[0].dateFrom)
  const dateTo = useAppSelector(state => state.flight.inputFlights[0].dateTo)
  const searchingFrom = useAppSelector(state => state.flight.searchingFrom)
  const searchingTo = useAppSelector(state => state.flight.searchingTo)
  const { getValidDayjs } = useTETranslation()
  const { t } = useTranslation()

  useEffect(() => {
    if (!prefill) return
    if (didPrefillRef.current) return

    const hasAirports = airports && Object.keys(airports).length > 0
    if (!hasAirports) return

    const normalizeCode = (value?: string) => String(value || "").trim().toUpperCase()
    const startCode = normalizeCode(prefill.startPoint)
    const endCode = normalizeCode(prefill.endPoint)

    const findAirport = (code: string) => {
      if (!code) return null
      return (airports as any)?.[code] || (Object.values(airports as any).find((a: any) => a?.code === code) as any)
    }

    dispatch(flightActions.updateModeSearchFlight({ isMultiCities: false }))
    dispatch(flightActions.changeCheckedItinerary(false))

    // DES_ROUTE: only prefill destination, avoid showing default origin (SGN)
    if (!startCode && !!endCode) {
      dispatch(
        flightActions.selectFromAirport({
          indexInputFlight: 0,
          fromAirport: undefined as any,
        })
      )
    }

    if (startCode) {
      const fromAirport = findAirport(startCode)
      if (fromAirport) dispatch(flightActions.selectFromAirport({ indexInputFlight: 0, fromAirport }))
    }

    if (endCode) {
      const toAirport = findAirport(endCode)
      if (toAirport) dispatch(flightActions.selectToAirport({ indexInputFlight: 0, toAirport }))
    }

    if (prefill.dateFrom) {
      const d = dayjs(prefill.dateFrom)
      dispatch(
        flightActions.changeDateFrom({
          indexInputFlight: 0,
          dateFrom: (d.isValid() ? d : dayjs()).format("YYYY-MM-DD"),
        })
      )
    }

    didPrefillRef.current = true
  }, [airports, dispatch, prefill])

  return (
    <>
      <PopupSelectPassenger ref={(curr) => { searchFlightDrawers.selectPassenger = curr; }} />
      <PopupSelectClass ref={(curr) => { searchFlightDrawers.selectClass = curr; }} />
      <DrawerSelectPlacement
        mode="to"
        value={selectedInputFlight?.toAirport}
        onChange={(airport) => {
          dispatch(flightActions.resetResultsFlight())
          setTimeout(() => {
            getMinFlights(dateFrom)
          }, 200)
          dispatch(flightActions.selectToAirport({ indexInputFlight: selectedFligthInputIndex, toAirport: airport }))
          dispatch(flightActions.searchAirport({
            value: "",
            searchPopular: true
          }))
        }}
        chooseCurrentLy={(from, to) => {
          dispatch(flightActions.selectToAirport({ indexInputFlight: selectedFligthInputIndex, toAirport: to }))
          dispatch(flightActions.selectFromAirport({ indexInputFlight: selectedFligthInputIndex, fromAirport: from }))
        }}
        onSearch={(val) => {
          if (val) {
            if (!searchingTo) {
              dispatch(flightActions.updateSearchingTo(true))
            }
          } else {
            if (searchingTo) {
              dispatch(flightActions.updateSearchingTo(false))
            }
          }
        }}
        ref={(curr) => { searchFlightDrawers.selectPlacementTo = curr; }}
        searching={searchingTo}
      />
      <DrawerSelectPlacement
        mode="from"
        value={selectedInputFlight?.fromAirport}
        onChange={(airport) => {
          dispatch(flightActions.resetResultsFlight())
          setTimeout(() => {
            getMinFlights(dateFrom)
          }, 200)
          dispatch(flightActions.selectFromAirport({ indexInputFlight: selectedFligthInputIndex, fromAirport: airport }))
          dispatch(flightActions.searchAirport({
            value: "",
            searchPopular: true
          }))
          setTimeout(() => {
            searchFlightDrawers.selectPlacementTo?.open()
          }, 280)
        }}
        chooseCurrentLy={(from, to) => {
          dispatch(flightActions.selectToAirport({ indexInputFlight: selectedFligthInputIndex, toAirport: to }))
          dispatch(flightActions.selectFromAirport({ indexInputFlight: selectedFligthInputIndex, fromAirport: from }))
        }}
        onSearch={(val) => {
          if (val) {
            if (!searchingFrom) {
              dispatch(flightActions.updateSearchingFrom(true))
            }
          } else {
            if (searchingFrom) {
              dispatch(flightActions.updateSearchingFrom(false))
            }
          }
        }}
        ref={(curr) => { searchFlightDrawers.selectPlacementFrom = curr; }}
        searching={searchingFrom}
      />
      <DrawerSelectDate
        variant="flight"
        value={[getValidDayjs(dateFrom), getValidDayjs(dateTo)]}
        range={checkedItinerary}
        ref={(curr) => { searchFlightDrawers.selectDate = curr; }}
        onCalendarChange={(dateStr) => {
          getMinFlights(dateStr)
        }}
        subData={searchMin}
        onChange={([from, to]) => {
          dispatch(flightActions.changeDateFrom({ indexInputFlight: selectedFligthInputIndex, dateFrom: from }))
          if (to) {
            dispatch(flightActions.changeDateTo({ indexInputFlight: selectedFligthInputIndex, dateTo: to }))
            dispatch(flightActions.changeCheckedItinerary(true))
          }
        }}
      />

      {!hidePageHeader && (
        <PageHeaderMobile
          title={(
            <Row justify='center'>
              <Col>
                <Title level={5} style={{ margin: 0 }}>
                  {t("common.flight")}
                </Title>
              </Col>
            </Row>
          )}
          shownRight={false}
          onLeftClick={() => router.push('/')}
        />
      )}

      <Col span={24} style={{ height: autoHeight ? 'auto' : 'calc(100vh - 7rem)', minHeight: 500, marginBottom: '2rem' }}>
        <Row
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%'
          }}
        >
          <Image
            src="/static/form-search-banner-2x.png"
            alt="Banner form mobile search flights"
            width={375}
            height={200}
            style={{ width: '100%', height: 'auto' }}
          />
        </Row>
        <Row
          justify='center'
          className="home__searching-mobile--form"
        >
          <Col span={24} className="home__searching-mobile--form-box">

            <Row justify='center'>
              <Col span={24}>
                <Title level={2} style={{ paddingInline: '1.5rem' }}>
                  {t("home.title", { platform: process.env.NEXT_PUBLIC_PLATFORM_NAME })}
                </Title>
              </Col>
              <Col span={24} style={{ width: '100%', maxWidth: '100%', paddingInline: 0 }}>
                <FormSearchFlightMobile />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      {/* <PageDrawerMobile
        open={!isDesktop}
        styles={{
          content: {
            padding: 0
          }
        }}
        headerProps={{
          title: (
            <Row justify='center'>
              <Col>
                <Title level={5} style={{ margin: 0 }}>
                  Vé máy bay
                </Title>
              </Col>
            </Row>
          ),
          shownRight: false,
          onLeftClick() {
            router.push('/')
          }
        }}
        footer={(
          <Col span={24}>
            <Button
              onClick={() => {
                getFlightsAllAirlines({
                  onStart({ newSearchString }) {
                    searchFlightDrawers.flights?.open()
                    router.push(`/flights/result${newSearchString}`)
                  },
                })

              }}
              icon={<FontAwesomeIcon icon={faSearch} />}
              size='large'
              type='primary'
              block
            >
              Tìm kiếm chuyến bay
            </Button>
          </Col>
        )}
      >
        <Col span={24} style={{ height: 'calc(100vh - 7rem)', minHeight: 700 }}>
          <Row>
            <Image
              src="/static/form-search-banner-2x.png"
              alt="Banner form mobile search flights"
              width={375}
              height={200}
              style={{ width: '100%', height: 'auto' }}
            />
          </Row>
          <Row
            justify='center'
            className="home__searching-mobile--form"
          >
            <Col span={24} className="home__searching-mobile--form-box">

              <Row justify='center'>
                <Col span={24}>
                  <Title level={2} style={{ paddingInline: '1.5rem' }}>
                    Bay thật dễ, mua thật rẻ cùng {process.env.NEXT_PUBLIC_PLATFORM_NAME}
                  </Title>
                </Col>
                <Col span={24} style={{ maxWidth: '40rem' }}>
                  <FormSearchFlightMobile />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </PageDrawerMobile> */}
    </>
  )
}

export default DrawerFormSearchFlightSkeleton